import { formatRecoveryTime } from "@/lib/format";
import type { IncidentMitigation } from "@/types";
import { IncidentConfidencePanel } from "@/components/dashboard/IncidentConfidencePanel";

interface IncidentRecommendedActionPanelProps {
  mitigation: IncidentMitigation;
  confidenceReasons: string[];
  fallbackConfidence?: number | null;
  onExecutePrimary?: (actionId: string) => void;
  executing?: boolean;
}

export function IncidentRecommendedActionPanel({
  mitigation,
  confidenceReasons,
  fallbackConfidence,
  onExecutePrimary,
  executing = false,
}: IncidentRecommendedActionPanelProps) {
  const reasons = mitigation.confidenceReasons.length > 0 ? mitigation.confidenceReasons : confidenceReasons;
  const primaryId = mitigation.primaryActionId;

  return (
    <section className="iw-section iw-recommended">
      <h3 className="iw-section__title iw-section__title--prominent">Recommended Action</h3>
      <dl className="iw-recommended__grid">
        <div className="iw-recommended__row">
          <dt>What to do</dt>
          <dd>{mitigation.what || mitigation.primaryActionLabel}</dd>
        </div>
        <div className="iw-recommended__row">
          <dt>Why</dt>
          <dd>{mitigation.why || mitigation.summary}</dd>
        </div>
        <div className="iw-recommended__row">
          <dt>Expected outcome</dt>
          <dd>{mitigation.expectedOutcome || mitigation.summary}</dd>
        </div>
        {mitigation.expectedRecoverySeconds != null ? (
          <div className="iw-recommended__row">
            <dt>Estimated recovery</dt>
            <dd>{formatRecoveryTime(mitigation.expectedRecoverySeconds)}</dd>
          </div>
        ) : null}
        <div className="iw-recommended__row">
          <dt>Estimated risk</dt>
          <dd>{mitigation.estimatedRisk}</dd>
        </div>
        {mitigation.alternativeActions.length > 0 ? (
          <div className="iw-recommended__row">
            <dt>Alternatives</dt>
            <dd>{mitigation.alternativeActions.map((a) => a.label).join(", ")}</dd>
          </div>
        ) : null}
      </dl>
      {primaryId && onExecutePrimary && primaryId !== "view-logs" ? (
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: "0.75rem" }}
          disabled={executing}
          onClick={() => onExecutePrimary(primaryId)}
        >
          {executing
            ? "Running…"
            : `Execute · ${mitigation.estimatedRisk} risk${
                mitigation.expectedRecoverySeconds
                  ? ` · ~${Math.max(1, Math.round(mitigation.expectedRecoverySeconds / 60))} min`
                  : ""
              }`}
        </button>
      ) : null}
      <IncidentConfidencePanel
        confidence={mitigation.confidence ?? fallbackConfidence ?? null}
        reasons={reasons}
      />
    </section>
  );
}
